import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  FieldTest,
  FieldTestType,
  FieldTestStatus,
  FieldTestChecklistItem,
  CustomFieldTest,
  CustomFieldTestType,
  FieldTestFile,
  FIELD_TEST_TYPE_OPTIONS,
  CUSTOM_FIELD_TEST_TYPE_OPTIONS
} from '../models/field-test.model';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

/**
 * Field Test Stats from API
 */
interface FieldTestStats {
  passed: number;
  failed: number;
  pending: number;
  total: number;
}

/**
 * DTOs for creating and updating field tests
 */
export interface CreateFieldTestDto {
  title: string;
  testType: FieldTestType;
  aircraftId?: string;
  aircraftName?: string;
  pilotId?: string;
  pilotName?: string;
  testDate: string;
  location?: string;
  checklist: FieldTestChecklistItem[];
  notes?: string;
}

export interface UpdateFieldTestDto extends Partial<CreateFieldTestDto> {
  status?: FieldTestStatus;
}

export interface CreateCustomFieldTestDto {
  title: string;
  testType: CustomFieldTestType;
  category?: string;
  description?: string;
  applicableAircraft?: string;
  frequency?: string;
  requiresSignoff?: boolean;
  files?: { fileName: string; fileType: string; filePath: string; fileSize?: number }[];
}

/**
 * Upload URL Response
 * Note: filePath is just the path (no domain) - use getDownloadUrl() for private files
 */
interface UploadUrlResponse {
  uploadUrl: string;
  filePath: string;
}

@Injectable({
  providedIn: 'root'
})
export class FieldTestService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  // Signal-based state for field tests
  private fieldTestsSignal = signal<FieldTest[]>([]);
  private customTestsSignal = signal<CustomFieldTest[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private statsSignal = signal<FieldTestStats>({ passed: 0, failed: 0, pending: 0, total: 0 });

  // Public computed signals
  readonly fieldTests = computed(() => this.fieldTestsSignal());
  readonly customTests = computed(() => this.customTestsSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Computed stats from signal
  readonly passedCount = computed(() => this.statsSignal().passed);
  readonly failedCount = computed(() => this.statsSignal().failed);
  readonly pendingCount = computed(() => this.statsSignal().pending);

  // Type options
  readonly testTypeOptions = FIELD_TEST_TYPE_OPTIONS;
  readonly customTestTypeOptions = CUSTOM_FIELD_TEST_TYPE_OPTIONS;

  constructor() {
    this.loadFieldTests();
    this.loadCustomTests();
    this.loadStats();
  }

  // ============================================
  // FIELD TEST CRUD METHODS
  // ============================================

  /**
   * Load field tests from API
   */
  async loadFieldTests(options?: {
    testType?: string;
    status?: string;
    aircraftId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const params: any = { page: options?.page || 1, limit: options?.limit || 100 };
      if (options?.testType) params.testType = options.testType;
      if (options?.status) params.status = options.status;
      if (options?.aircraftId) params.aircraftId = options.aircraftId;
      if (options?.startDate) params.startDate = options.startDate;
      if (options?.endDate) params.endDate = options.endDate;

      const response = await firstValueFrom(
        this.http.get<ApiResponse<FieldTest[]>>(`${this.apiUrl}/field-tests`, { params })
      );

      if (response.success && response.data) {
        // Map API response to frontend model
        const tests = response.data.map(t => this.mapApiToFieldTest(t));
        this.fieldTestsSignal.set(tests);
      } else {
        throw new Error(response.error || 'Failed to load field tests');
      }
    } catch (error: any) {
      console.error('Error loading field tests:', error);
      this.errorSignal.set(error.message || 'Failed to load field tests');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Load stats from API
   */
  async loadStats(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<FieldTestStats>>(`${this.apiUrl}/field-tests/stats`)
      );

      if (response.success && response.data) {
        this.statsSignal.set(response.data);
      }
    } catch (error: any) {
      console.error('Error loading field test stats:', error);
    }
  }

  getFieldTests(): FieldTest[] {
    return this.fieldTestsSignal();
  }

  getFieldTestById(id: string): FieldTest | undefined {
    return this.fieldTestsSignal().find(t => t.id === id);
  }

  /**
   * Fetch single field test from API
   */
  async fetchFieldTestById(id: string): Promise<FieldTest | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<FieldTest>>(`${this.apiUrl}/field-tests/${id}`)
      );

      if (response.success && response.data) {
        return this.mapApiToFieldTest(response.data);
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching field test:', error);
      return null;
    }
  }

  /**
   * Create field test via API
   */
  async createFieldTest(dto: CreateFieldTestDto): Promise<FieldTest | null> {
    this.loadingSignal.set(true);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<FieldTest>>(`${this.apiUrl}/field-tests`, dto)
      );

      if (response.success && response.data) {
        const newTest = this.mapApiToFieldTest(response.data);
        this.fieldTestsSignal.update(tests => [newTest, ...tests]);
        this.loadStats(); // Refresh stats
        return newTest;
      }
      throw new Error(response.error || 'Failed to create field test');
    } catch (error: any) {
      console.error('Error creating field test:', error);
      this.errorSignal.set(error.message || 'Failed to create field test');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update field test via API
   */
  async updateFieldTest(id: string, dto: UpdateFieldTestDto): Promise<FieldTest | null> {
    this.loadingSignal.set(true);

    try {
      const response = await firstValueFrom(
        this.http.put<ApiResponse<FieldTest>>(`${this.apiUrl}/field-tests/${id}`, dto)
      );

      if (response.success && response.data) {
        const updatedTest = this.mapApiToFieldTest(response.data);
        this.fieldTestsSignal.update(tests =>
          tests.map(t => t.id === id ? updatedTest : t)
        );
        this.loadStats(); // Refresh stats
        return updatedTest;
      }
      throw new Error(response.error || 'Failed to update field test');
    } catch (error: any) {
      console.error('Error updating field test:', error);
      this.errorSignal.set(error.message || 'Failed to update field test');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Delete field test via API
   */
  async deleteFieldTest(id: string): Promise<boolean> {
    this.loadingSignal.set(true);

    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(`${this.apiUrl}/field-tests/${id}`)
      );

      if (response.success) {
        this.fieldTestsSignal.update(tests => tests.filter(t => t.id !== id));
        this.loadStats(); // Refresh stats
        return true;
      }
      throw new Error(response.error || 'Failed to delete field test');
    } catch (error: any) {
      console.error('Error deleting field test:', error);
      this.errorSignal.set(error.message || 'Failed to delete field test');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  // ============================================
  // CUSTOM FIELD TEST SUBMISSION CRUD METHODS
  // ============================================

  /**
   * Load custom field test submissions from API
   */
  async loadCustomTests(page: number = 1, limit: number = 100): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<CustomFieldTest[]>>(`${this.apiUrl}/field-test-submissions`, {
          params: { page, limit }
        })
      );

      if (response.success && response.data) {
        const submissions = response.data.map(t => this.mapApiToCustomTest(t));
        this.customTestsSignal.set(submissions);
      }
    } catch (error: any) {
      console.error('Error loading custom tests:', error);
    }
  }

  getCustomTests(): CustomFieldTest[] {
    return this.customTestsSignal();
  }

  getCustomTestById(id: string): CustomFieldTest | undefined {
    return this.customTestsSignal().find(t => t.id === id);
  }

  /**
   * Fetch single custom test from API
   */
  async fetchCustomTestById(id: string): Promise<CustomFieldTest | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<any>>(`${this.apiUrl}/field-test-submissions/${id}`)
      );

      if (response.success && response.data) {
        return this.mapApiToCustomTest(response.data);
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching custom test:', error);
      return null;
    }
  }

  /**
   * Get pre-signed upload URL for file
   * Returns uploadUrl (for PUT to S3) and filePath (path to store in DB)
   */
  async getUploadUrl(fileName: string, fileType: string): Promise<UploadUrlResponse | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<UploadUrlResponse>>(`${this.apiUrl}/field-test-submissions/upload-url`, {
          fileName,
          fileType
        })
      );

      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error: any) {
      console.error('Error getting upload URL:', error);
      return null;
    }
  }

  /**
   * Upload file to S3 using pre-signed URL
   * Files are uploaded as private (no public ACL header)
   */
  async uploadFile(uploadUrl: string, file: File): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type
          }
        })
      );
      return true;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      return false;
    }
  }

  /**
   * Get pre-signed download URL for a private file
   * @param filePath - The stored file path (without CDN domain)
   * @returns Temporary signed URL for downloading/viewing the file
   */
  async getDownloadUrl(filePath: string): Promise<string | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<{ downloadUrl: string }>>(`${this.apiUrl}/field-test-submissions/download-url`, {
          filePath
        })
      );

      if (response.success && response.data) {
        return response.data.downloadUrl;
      }
      return null;
    } catch (error: any) {
      console.error('Error getting download URL:', error);
      return null;
    }
  }

  /**
   * Create custom test submission via API
   */
  async createCustomTest(dto: CreateCustomFieldTestDto): Promise<CustomFieldTest | null> {
    this.loadingSignal.set(true);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<any>>(`${this.apiUrl}/field-test-submissions`, dto)
      );

      if (response.success && response.data) {
        const newTest = this.mapApiToCustomTest(response.data);
        this.customTestsSignal.update(tests => [newTest, ...tests]);
        return newTest;
      }
      throw new Error(response.error || 'Failed to create custom test');
    } catch (error: any) {
      console.error('Error creating custom test:', error);
      this.errorSignal.set(error.message || 'Failed to create custom test');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update custom test submission via API
   */
  async updateCustomTest(id: string, dto: Partial<CreateCustomFieldTestDto>): Promise<CustomFieldTest | null> {
    this.loadingSignal.set(true);

    try {
      const response = await firstValueFrom(
        this.http.put<ApiResponse<any>>(`${this.apiUrl}/field-test-submissions/${id}`, dto)
      );

      if (response.success && response.data) {
        const updatedTest = this.mapApiToCustomTest(response.data);
        this.customTestsSignal.update(tests =>
          tests.map(t => t.id === id ? updatedTest : t)
        );
        return updatedTest;
      }
      throw new Error(response.error || 'Failed to update custom test');
    } catch (error: any) {
      console.error('Error updating custom test:', error);
      this.errorSignal.set(error.message || 'Failed to update custom test');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Delete custom test submission via API
   */
  async deleteCustomTest(id: string): Promise<boolean> {
    this.loadingSignal.set(true);

    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(`${this.apiUrl}/field-test-submissions/${id}`)
      );

      if (response.success) {
        this.customTestsSignal.update(tests => tests.filter(t => t.id !== id));
        return true;
      }
      throw new Error(response.error || 'Failed to delete custom test');
    } catch (error: any) {
      console.error('Error deleting custom test:', error);
      this.errorSignal.set(error.message || 'Failed to delete custom test');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Add a file to an existing custom test submission
   */
  async addFileToCustomTest(
    submissionId: string,
    file: { fileName: string; fileType: string; filePath: string; fileSize?: number }
  ): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<any>>(`${this.apiUrl}/field-test-submissions/${submissionId}/files`, file)
      );
      return response.success;
    } catch (error: any) {
      console.error('Error adding file to custom test:', error);
      return false;
    }
  }

  /**
   * Remove a file from an existing custom test submission
   */
  async removeFileFromCustomTest(submissionId: string, fileId: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(`${this.apiUrl}/field-test-submissions/${submissionId}/files/${fileId}`)
      );
      return response.success;
    } catch (error: any) {
      console.error('Error removing file from custom test:', error);
      return false;
    }
  }

  /**
   * Increment usage count for submission
   */
  async incrementUsage(id: string): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<ApiResponse<void>>(`${this.apiUrl}/field-test-submissions/${id}/increment-usage`, {})
      );
    } catch (error: any) {
      console.error('Error incrementing usage:', error);
    }
  }

  // ============================================
  // MAPPING METHODS
  // ============================================

  private mapApiToFieldTest(data: any): FieldTest {
    return {
      id: data.id,
      title: data.title,
      testType: data.testType || data.test_type,
      aircraftId: data.aircraftId || data.aircraft_id,
      aircraft: data.aircraftName || data.aircraft_name || '',
      pilotId: data.pilotId || data.pilot_id,
      pilot: data.pilotName || data.pilot_name || '',
      date: data.testDate || data.test_date,
      location: data.location || '',
      status: data.status,
      checklist: data.checklist || [],
      notes: data.notes
    };
  }

  private mapApiToCustomTest(data: any): CustomFieldTest {
    return {
      id: data.id,
      title: data.title,
      testType: data.testType || data.test_type,
      category: data.category || '',
      datePerformed: data.createdAt || data.created_at,
      description: data.description,
      files: (data.files || []).map((f: any) => ({
        id: f.id,  // Include file ID for delete operations
        name: f.fileName || f.file_name,
        type: f.fileType || f.file_type,
        // Support both old (file_url) and new (filePath) field names
        filePath: f.filePath || f.file_path || f.fileUrl || f.file_url,
        size: f.fileSize || f.file_size
      })),
      applicableAircraft: data.applicableAircraft || data.applicable_aircraft || '',
      frequency: data.frequency || '',
      requiresSignoff: data.requiresSignoff || data.requires_signoff || false,
      createdDate: data.createdAt || data.created_at,
      usageCount: data.usageCount || data.usage_count || 0
    };
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  private calculateTestStatus(checklist: FieldTestChecklistItem[]): FieldTestStatus {
    if (checklist.length === 0) return 'pending';

    const allPassed = checklist.every(item => item.passed);
    const somePending = checklist.some(item => !item.passed && !item.notes);

    if (allPassed) return 'passed';
    if (somePending) return 'pending';
    return 'failed';
  }

  getStatusText(status: FieldTestStatus): string {
    switch (status) {
      case 'passed': return 'Passed';
      case 'failed': return 'Failed';
      case 'pending': return 'Pending';
      default: return status;
    }
  }

  getTestTypeBadgeClass(type: FieldTestType): string {
    switch (type) {
      case 'pre-flight': return 'type-preflight';
      case 'post-flight': return 'type-postflight';
      case 'equipment': return 'type-equipment';
      case 'calibration': return 'type-calibration';
      default: return 'bg-secondary';
    }
  }

  getTestTypeLabel(type: FieldTestType): string {
    return this.testTypeOptions.find(t => t.value === type)?.label || type;
  }

  getCustomTestTypeIcon(type: CustomFieldTestType): string {
    switch (type) {
      case 'nozzle-calibration': return 'bi-droplet-half';
      case 'equipment-inspection': return 'bi-clipboard-check';
      case 'spray-pattern': return 'bi-grid-3x3';
      case 'flow-rate': return 'bi-speedometer2';
      case 'tank-calibration': return 'bi-bucket';
      case 'other': return 'bi-file-earmark';
      default: return 'bi-file-earmark';
    }
  }

  getCustomTestTypeLabel(type: CustomFieldTestType): string {
    return this.customTestTypeOptions.find(t => t.value === type)?.label || type;
  }

  getCategoryLabel(category: string): string {
    const labels: Record<string, string> = {
      'pre-season': 'Pre-Season',
      'in-season': 'In-Season',
      'post-season': 'Post-Season',
      'maintenance': 'Maintenance'
    };
    return labels[category] || category;
  }

  getFrequencyLabel(frequency: string): string {
    const labels: Record<string, string> = {
      'as-needed': 'As Needed',
      'every-flight': 'Every Flight',
      'daily': 'Daily',
      'weekly': 'Weekly',
      'monthly': 'Monthly',
      'annually': 'Annually'
    };
    return labels[frequency] || frequency;
  }

  getFileIcon(type: string): string {
    if (type === 'application/pdf' || type === 'pdf') return 'bi-file-earmark-pdf';
    if (type.startsWith('image/') || ['jpg', 'jpeg', 'png'].includes(type)) return 'bi-file-earmark-image';
    if (type.includes('word') || ['doc', 'docx'].includes(type)) return 'bi-file-earmark-word';
    return 'bi-file-earmark';
  }

  getPassedChecklistCount(test: FieldTest): number {
    return test.checklist.filter(c => c.passed).length;
  }
}
